import { useStore } from '@data/other/withStore';
import styled from '@emotion/styled';
import BELangCurrencySelect from '@view/components/Popovers/LangCurrencySelect';
import PromoCode from '@view/components/PromoCode';
import { observer } from 'mobx-react';
import { useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Routes as AppRoutes, getPropertyRoot } from '../routes';
import BEImage from '@molecules/BEImage';
import Text from '@molecules/Text';
import { Box, Flex } from '@chakra-ui/react';
import { useNavigate } from '@view/hooks/useNavigate';
import { useAlert } from '@molecules/BEOffScreen/Modal';
import type { Currencies, Languages } from '@data/types/common';
import { useReachScrollY } from '@view/hooks/useReachScrollY';
import { useViewportSize } from '@view/hooks/useViewportSize';
import { usePageNavigation } from '@view/hooks/usePageNavigation';

const LANGUAGES: Array<{ text: string; value: Languages }> = [
  {
    text: 'English',
    value: 'en',
  },
  {
    text: 'Português',
    value: 'pt-br',
  },
  // {
  //   text: 'Español',
  //   value: 'es',
  // },
  // {
  //   text: 'Українська',
  //   value: 'ru',
  // },
  // {
  //   text: 'Deutsch',
  //   value: 'de',
  // },
];

const CURRENCIES: Array<{
  shortText: string;
  longText: string;
  value: Currencies;
}> = [
  {
    shortText: 'USD',
    longText: 'US Dollar',
    value: 'usd',
  },
  {
    shortText: 'BRL',
    longText: 'Brazilian Real',
    value: 'brl',
  },
  {
    shortText: 'EUR',
    longText: 'Euros',
    value: 'eur',
  },
  {
    shortText: 'UYU',
    longText: 'Uruguayan Peso',
    value: 'uyu',
  },
  {
    shortText: 'UZS',
    longText: 'Uzbekistani Som',
    value: 'uzs',
  },
  {
    shortText: 'VEF',
    longText: 'Venezuelan Bolivar Fuerte',
    value: 'vef',
  },
  {
    shortText: 'ARS',
    longText: 'Argentine Peso',
    value: 'ars',
  },
  {
    shortText: 'AOA',
    longText: 'Angolan Kwanza',
    value: 'aoa',
  },
];

const Header = () => {
  const { propertyCode } = useParams();

  const { property, components } = useStore();
  const { accommodations, cart } = property;
  const showDatePicker = useReachScrollY(512);
  const { width } = useViewportSize();
  const { isHomePage } = usePageNavigation();

  const alert = useAlert();

  const navigate = useNavigate();
  const onClickSearch = useCallback(
    async (checkin?: Date, checkout?: Date, code?: string) => {
      if (cart.items.length > 0 && checkin && checkout) {
        if (
          cart.checkIn?.getTime() !== checkin.getTime() ||
          cart.checkOut?.getTime() !== checkout.getTime()
        ) {
          const result = await alert({
            color: 'error',
            title: 'Are you sure?',
            content:
              'Changing your stay date will clear your current accommodations',
            okText: 'Clear',
            cancelText: 'Close',
          });

          if (!result) return;
        }
      }

      cart.clear();
      components.promoCode.set({ checkin, checkout, code });
      await accommodations.search(
        components.promoCode.value.checkin,
        components.promoCode.value.checkout
      );
      navigate(AppRoutes.Accommodations);
    },
    [cart, components.promoCode, accommodations, navigate, alert]
  );

  return (
    <StyledHeader boxShadow={'sm'}>
      <div className="row">
        <Link to={getPropertyRoot(true)}>
          <Flex alignItems="center" gap="16px">
            <BEImage
              src={property.model?.picture?.src}
              width={width >= 768 ? 84 : 48}
              height={width >= 768 ? 84 : 48}
              fit="cover"
              borderRadius="8px"
            />
            <Text className="property-name" type="h1" size="xl" weight="500">
              {property.model?.name}
            </Text>
          </Flex>
        </Link>
        <div className="date-picker-container">
          {width > 640 && (!isHomePage || showDatePicker) && (
            <PromoCode size="md" onClickSearch={onClickSearch} />
          )}
        </div>
        <BELangCurrencySelect
          size={width >= 768 ? 'lg' : 'md'}
          languages={LANGUAGES}
          currencies={CURRENCIES}
        />
      </div>
      {width <= 640 && (!isHomePage || showDatePicker) && (
        <Box m="8px">
          <PromoCode size="sm" onClickSearch={onClickSearch} />
        </Box>
      )}
    </StyledHeader>
  );
};

const StyledHeader = styled(Box)`
  min-height: 70px;
  width: 100%;
  background-color: white;
  top: 0;
  left: 0;
  position: sticky;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  z-index: 2; // ShoppingCart mobile BottomSheet z-index: 3, so to keep the bottom sheet over the header, changed from 100 to 2

  .row {
    padding: 8px 24px;
    background-color: white;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .date-picker-container {
    flex-grow: 1;
    display: flex;
    justify-content: center;
    min-height: 56px;
  }
`;

export default observer(Header);
